import { TokenCopyField } from '@/components/admin/reglages/token-copy-field';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { bashCommands, powershellCommands } from '@/lib/token-labels';
import type { OperatorTokenMinted } from '@matter_extractor/shared';

/**
 * The ONE panel of this application that shows a secret (§20.2, issue #84).
 *
 * ## It lives in component state, and nowhere else
 *
 * Not `localStorage`, not the URL, not a query cache, not a log. The value
 * arrives in the answer of `POST /api/admin/tokens`, is held by the page for
 * as long as this panel is mounted, and is gone the moment it closes or the
 * operator leaves the tab. That is not caution for its own sake: the base
 * holds only a SHA-256 fingerprint, so anything this repo wrote the clear
 * value into would become the only copy of a live credential - and the only
 * one nobody rotates.
 *
 * The consequence is stated on the panel rather than left to be discovered:
 * « il ne sera plus jamais affiché ». A lost token is re-minted, not
 * recovered, and an operator who knows that before closing the panel copies it
 * now.
 *
 * ## The commands are here because the token is shown once
 *
 * An operator who has to invent `MATTER_PAT` from a paragraph gets exactly one
 * attempt, and the panel is gone by the time they find out they mistyped it.
 * Both shells are given because both are used on this team, and neither is
 * this screen's to choose. The words themselves are in `lib/token-labels.ts`,
 * pure and testable.
 *
 * ## The API address is the one the BUNDLE calls, not the page's origin
 *
 * `apiBaseUrl()` of `lib/tokens-api.ts`, read by the route and passed in:
 * `VITE_API_URL` when it is set, the page's origin when it is empty. That is
 * the same derivation `chatSocketUrl` makes, and it is the only honest value to
 * print - `MATTER_API_URL` is the address `.mcp.json` will reach, so on a
 * two-port dev stack the page's own origin would wire Claude Code to the Vite
 * server, from a screen that told the operator to paste it.
 */

export interface TokenMintedProps {
  readonly minted: OperatorTokenMinted;
  /** Where the API is, as the bundle calls it - `apiBaseUrl()` of `tokens-api`. */
  readonly apiUrl: string;
  readonly onClose: () => void;
}

export function TokenMinted({ minted, apiUrl, onClose }: TokenMintedProps) {
  return (
    <section
      data-testid="jetons-nouveau"
      aria-labelledby="jetons-nouveau-titre"
      className="space-y-4 rounded-md border-2 border-bleu-nuit bg-white p-5"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 id="jetons-nouveau-titre" className="font-bold text-bleu-nuit text-xl">
          Jeton frappé : « {minted.label} »
        </h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="jetons-nouveau-fermer"
          onClick={onClose}
        >
          Fermer
        </Button>
      </div>

      <Callout tone="warn" title="Il ne sera plus jamais affiché" live="status">
        Copiez-le maintenant. Le serveur n’en garde que l’empreinte, donc un jeton perdu se
        refrappe, il ne se retrouve pas. C’est un secret : il ouvre tout ce que votre session ouvre,
        et ce qu’il écrit est signé de votre adresse.
      </Callout>

      <TokenCopyField
        value={minted.token}
        label="Jeton personnel"
        buttonLabel="Copier le jeton"
        testId="jetons-nouveau-valeur"
      />

      <div className="space-y-2">
        <h4 className="font-semibold text-bleu-nuit text-sm">PowerShell</h4>
        <TokenCopyField
          value={powershellCommands(minted.token, apiUrl)}
          label="Commandes PowerShell"
          buttonLabel="Copier les commandes"
          rows={2}
          testId="jetons-commandes-powershell"
        />
        <p className="text-gray-600 text-xs">
          <code className="font-mono">setx</code> écrit les variables pour les prochains terminaux :
          ouvrez-en un neuf avant de lancer Claude Code.
        </p>
      </div>

      <div className="space-y-2">
        <h4 className="font-semibold text-bleu-nuit text-sm">bash</h4>
        <TokenCopyField
          value={bashCommands(minted.token, apiUrl)}
          label="Commandes bash"
          buttonLabel="Copier les commandes"
          rows={2}
          testId="jetons-commandes-bash"
        />
      </div>

      <p className="text-gray-600 text-xs">
        <code className="font-mono">MATTER_API_URL</code> vaut l’adresse de l’API que cette page
        appelle.
      </p>
    </section>
  );
}
